<section class="passenger-profiles-page" *ngIf="!featureAvailable">
  <div class="empty-state">
    <i class="material-icons">block</i>
    <h2>Passenger Profiles is currently unavailable</h2>
    <p>{{ featureUnavailableMessage }}</p>
    <a class="filter-action" routerLink="/dashboard">Return to dashboard</a>
  </div>
</section>
<section class="passenger-profiles-page" *ngIf="featureAvailable">
  <div class="page-header">
    <div>
      <p class="eyebrow">Passenger intelligence</p>
      <h2>Passenger profiles</h2>
      <p>Normalized passenger records from app registration, hotspot data, imported records, or combined sources.</p>
    </div>
    <div class="future-hooks" aria-label="Planned passenger intelligence hooks">
      <span><i class="material-icons">sync</i>Sync status</span>
      <span><i class="material-icons">call_merge</i>Merge suggestions</span>
      <span><i class="material-icons">wifi_tethering</i>Hotspot sessions</span>
      <span><i class="material-icons">route</i>Trip registry</span>
    </div>
  </div>

  <div class="profiles-layout">
    <section class="profiles-panel">
      <div class="panel-toolbar">
        <div>
          <p class="eyebrow">Profiles</p>
          <h3>{{ getPageSummary() }}</h3>
        </div>
        <div class="filter-row">
          <label class="search-box">
            <i class="material-icons">search</i>
            <input type="search" placeholder="Search name, phone, or email" [(ngModel)]="searchTerm" (keyup.enter)="applyFilters()">
          </label>
          <select class="source-filter" [(ngModel)]="selectedSource" (ngModelChange)="applyFilters()">
            <option *ngFor="let source of sourceFilters" [ngValue]="source.value">{{ source.label }}</option>
          </select>
          <button class="filter-action" type="button" (click)="applyFilters()">
            <i class="material-icons">search</i>
            <span>Search</span>
          </button>
        </div>
      </div>

      <div class="alert error" *ngIf="listError">
        <i class="material-icons">error</i>
        <span>{{ listError }}</span>
      </div>

      <div class="loading-state" *ngIf="isLoadingList">
        <i class="material-icons">sync</i>
        <span>Loading passenger profiles...</span>
      </div>

      <div class="table-scroll" *ngIf="!isLoadingList && profiles.length; else noProfiles">
        <table>
          <thead>
            <tr>
              <th>Display name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Primary source</th>
              <th>Linked app user</th>
              <th>First seen</th>
              <th>Last seen</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let profile of profiles; trackBy: trackProfile"
                [class.selected]="selectedProfile?.id === profile.id"
                (click)="selectProfile(profile)">
              <td>
                <strong>{{ profile.displayName }}</strong>
                <small [class.verified]="profile.linkedAppUser?.verified">{{ getProfileKind(profile) }}</small>
              </td>
              <td>{{ profile.phone || '-' }}</td>
              <td>{{ profile.email || '-' }}</td>
              <td><span class="status-badge" [ngClass]="'source-' + profile.primarySource.toLowerCase()">{{ profile.primarySource }}</span></td>
              <td><span class="status-badge" [class.linked]="profile.linkedAppUser" [class.unlinked]="!profile.linkedAppUser">{{ getLinkedState(profile) }}</span></td>
              <td>{{ formatDate(profile.firstSeen) }}</td>
              <td>{{ formatDate(profile.lastSeen) }}</td>
              <td>{{ formatDate(profile.updatedDate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-bar" *ngIf="!isLoadingList && meta.totalPages > 1">
        <button type="button" class="pager-button" [disabled]="meta.page === 1" (click)="changePage(meta.page - 1)">
          <i class="material-icons">chevron_left</i>
          <span>Previous</span>
        </button>
        <span>Page {{ meta.page }} of {{ meta.totalPages }}</span>
        <button type="button" class="pager-button" [disabled]="meta.page === meta.totalPages" (click)="changePage(meta.page + 1)">
          <span>Next</span>
          <i class="material-icons">chevron_right</i>
        </button>
      </div>

      <ng-template #noProfiles>
        <div class="empty-state" *ngIf="!isLoadingList && !hasActiveFilters()">
          <i class="material-icons">person_search</i>
          <h3>No profiles yet</h3>
          <p>Passenger profiles will appear here once app, hotspot, or imported passenger data is available.</p>
        </div>
        <div class="empty-state" *ngIf="!isLoadingList && hasActiveFilters()">
          <i class="material-icons">filter_alt_off</i>
          <h3>No results for filters</h3>
          <p>No passenger profiles match the current search and source filter.</p>
          <button class="filter-action" type="button" (click)="clearFilters()">
            <i class="material-icons">close</i>
            <span>Clear filters</span>
          </button>
        </div>
      </ng-template>
    </section>

    <aside class="detail-panel" *ngIf="isLoadingDetail">
      <div class="empty-state">
        <i class="material-icons">sync</i>
        <h3>Loading profile detail</h3>
        <p>Fetching identity, contact, linked user, and external identity records.</p>
      </div>
    </aside>

    <aside class="detail-panel" *ngIf="!isLoadingDetail && detailError">
      <div class="empty-state">
        <i class="material-icons">person_off</i>
        <h3>{{ detailError }}</h3>
        <p *ngIf="detailError === 'Profile not found.'">The requested passenger profile could not be found.</p>
        <p *ngIf="detailError !== 'Profile not found.'">Try refreshing the page or selecting another passenger profile.</p>
      </div>
    </aside>

    <aside class="detail-panel" *ngIf="!isLoadingDetail && !detailError && selectedProfile">
      <div class="detail-header">
        <div>
          <p class="eyebrow">Profile detail</p>
          <h3>{{ selectedProfile.displayName }}</h3>
          <span class="profile-id">{{ selectedProfile.id }}</span>
        </div>
        <div class="profile-actions">
          <span class="status-badge" [class.linked]="selectedProfile.linkedAppUser" [class.unlinked]="!selectedProfile.linkedAppUser">
            {{ getLinkedState(selectedProfile) }}
          </span>
          <button class="filter-action" type="button" *ngIf="canCreateDriverFromProfile" (click)="startCreateDriverFromProfile()">
            <i class="material-icons">person_add</i>
            <span>Create driver</span>
          </button>
        </div>
      </div>

      <div class="alert success" *ngIf="createDriverSuccess">
        <i class="material-icons">check_circle</i>
        <span>{{ createDriverSuccess }}</span>
      </div>
      <div class="driver-create-panel" *ngIf="showCreateDriverForm">
        <div class="alert error" *ngIf="createDriverError">
          <i class="material-icons">error</i>
          <span>{{ createDriverError }}</span>
        </div>
        <div class="detail-grid">
          <div>
            <dt>Driver name</dt>
            <dd>{{ getCreateDriverNamePreview() }}</dd>
          </div>
          <label *ngIf="isAdminOrPlatform">
            <dt>Organisation</dt>
            <dd>
              <select class="source-filter" [(ngModel)]="createDriverOrganisationID">
                <option [ngValue]="null">Select organisation</option>
                <option *ngFor="let organisation of organisations" [ngValue]="organisation.organisationID">
                  {{ organisation.organisationName || organisation.orgName || ('Organisation ' + organisation.organisationID) }}
                </option>
              </select>
            </dd>
          </label>
          <label>
            <dt>Username</dt>
            <dd><input class="source-filter" type="text" [(ngModel)]="createDriverUsername"></dd>
          </label>
          <label>
            <dt>Password</dt>
            <dd><input class="source-filter" type="password" [(ngModel)]="createDriverPassword" placeholder="Optional password"></dd>
          </label>
        </div>
        <div class="pagination-bar">
          <button class="pager-button" type="button" (click)="showCreateDriverForm = false">
            <i class="material-icons">close</i>
            <span>Cancel</span>
          </button>
          <button class="filter-action" type="button" [disabled]="isCreatingDriver" (click)="createDriverFromProfile()">
            <i class="material-icons">person_add</i>
            <span>{{ isCreatingDriver ? 'Creating...' : 'Create linked driver' }}</span>
          </button>
        </div>
      </div>

      <nav class="tabs" aria-label="Passenger profile detail sections">
        <button type="button"
                *ngFor="let tab of tabs"
                [class.active]="activeTab === tab.id"
                (click)="setActiveTab(tab.id)">
          <i class="material-icons">{{ tab.icon }}</i>
          <span>{{ tab.label }}</span>
        </button>
      </nav>

      <section class="tab-panel" *ngIf="activeTab === 'identity'">
        <dl class="detail-grid">
          <div>
            <dt>Display name</dt>
            <dd>{{ selectedProfile.displayName }}</dd>
          </div>
          <div>
            <dt>First name</dt>
            <dd>{{ selectedProfile.firstName || '-' }}</dd>
          </div>
          <div>
            <dt>Last name</dt>
            <dd>{{ selectedProfile.lastName || '-' }}</dd>
          </div>
          <div>
            <dt>Primary source</dt>
            <dd><span class="status-badge" [ngClass]="'source-' + selectedProfile.primarySource.toLowerCase()">{{ selectedProfile.primarySource }}</span></dd>
          </div>
          <div>
            <dt>First seen</dt>
            <dd>{{ formatDate(selectedProfile.firstSeen) }}</dd>
          </div>
          <div>
            <dt>Last seen</dt>
            <dd>{{ formatDate(selectedProfile.lastSeen) }}</dd>
          </div>
          <div>
            <dt>Updated</dt>
            <dd>{{ formatDate(selectedProfile.updatedDate) }}</dd>
          </div>
          <div class="wide">
            <dt>Profile classification</dt>
            <dd>{{ getProfileKind(selectedProfile) }}</dd>
          </div>
        </dl>
      </section>

      <section class="tab-panel" *ngIf="activeTab === 'contact'">
        <dl class="detail-grid">
          <div>
            <dt>Phone</dt>
            <dd>{{ selectedProfile.phone || '-' }}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{{ selectedProfile.email || '-' }}</dd>
          </div>
          <div class="wide">
            <dt>Source notes</dt>
            <dd>{{ selectedProfile.notes || 'No additional source notes recorded.' }}</dd>
          </div>
        </dl>
      </section>

      <section class="tab-panel" *ngIf="activeTab === 'appUser'">
        <dl class="detail-grid" *ngIf="selectedProfile.linkedAppUser; else noLinkedUser">
          <div>
            <dt>User ID</dt>
            <dd>{{ selectedProfile.linkedAppUser.id }}</dd>
          </div>
          <div>
            <dt>Username</dt>
            <dd>{{ selectedProfile.linkedAppUser.username }}</dd>
          </div>
          <div>
            <dt>User state</dt>
            <dd><span class="status-badge linked">{{ getLinkedState(selectedProfile) }}</span></dd>
          </div>
          <div>
            <dt>Last login</dt>
            <dd>{{ formatDate(selectedProfile.linkedAppUser.lastLogin) }}</dd>
          </div>
        </dl>
        <ng-template #noLinkedUser>
          <div class="empty-state compact">
            <i class="material-icons">link_off</i>
            <h3>No linked app user</h3>
            <p>This commuter has been discovered outside verified app registration.</p>
          </div>
        </ng-template>
      </section>

      <section class="tab-panel" *ngIf="activeTab === 'externalIdentities'">
        <div class="alert error" *ngIf="externalIdentitiesError">
          <i class="material-icons">error</i>
          <span>{{ externalIdentitiesError }}</span>
        </div>
        <div class="identity-table" *ngIf="selectedProfile.externalIdentities.length; else noIdentities">
          <table>
            <thead>
              <tr>
                <th>Provider</th>
                <th>Entity type</th>
                <th>External ID</th>
                <th>Status</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Online</th>
                <th>Last online</th>
                <th>First synced</th>
                <th>Last synced</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let identity of selectedProfile.externalIdentities; trackBy: trackIdentity">
                <td>{{ identity.provider }}</td>
                <td>{{ identity.entityType }}</td>
                <td><strong>{{ identity.externalId }}</strong></td>
                <td><span class="status-badge">{{ identity.status }}</span></td>
                <td>{{ getIdentityPhone(identity) }}</td>
                <td>{{ getIdentityEmail(identity) }}</td>
                <td><span class="status-badge" [ngClass]="'online-' + identity.onlineState.toLowerCase()">{{ identity.onlineState }}</span></td>
                <td>{{ formatDate(identity.lastOnline) }}</td>
                <td>{{ formatDate(identity.firstSynced) }}</td>
                <td>{{ formatDate(identity.lastSynced) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <ng-template #noIdentities>
          <div class="empty-state compact">
            <i class="material-icons">hub</i>
            <h3>No external identities linked</h3>
            <p>Provider-specific records such as Powerlynx customers will be listed here.</p>
          </div>
        </ng-template>
      </section>
    </aside>

    <aside class="detail-panel" *ngIf="!isLoadingDetail && !detailError && !selectedProfile">
        <div class="empty-state">
          <i class="material-icons">person_search</i>
          <h3>No profile selected</h3>
          <p>Select a passenger profile to inspect identity, contact, linked user, and provider records.</p>
        </div>
    </aside>
  </div>
</section>
